import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import TablePagination from '@mui/material/TablePagination'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import BlockIcon from '@mui/icons-material/Block'
import SearchIcon from '@mui/icons-material/Search'
import type { IQuickInstrumentListItem } from '@brushfire/types'
import { useToast } from '@/shared/toast/ToastProvider'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'
import {
  DIRECT_INSTRUMENT_STATUS_FILTERS,
  type DirectInstrumentStatusFilter,
  isSignupSurveyPausedAtTarget,
} from '../lib/quick-instrument-display'
import {
  usePauseQuickInstrument,
  useReactivateQuickInstrument,
  useStopQuickInstrument,
} from '../api/quick-instruments-mutations'
import { useQuickInstruments } from '../api/quick-instruments-queries'
import { useLiveEditEntry } from '../hooks/useLiveEditEntry'
import { DirectInstrumentCard } from './DirectInstrumentCard'
import { PauseInstrumentDialog } from './PauseInstrumentDialog'
import { ResumeSurveyDialog } from './ResumeSurveyDialog'
import { StopInstrumentDialog } from './StopInstrumentDialog'

const VIOLET = '#7C3AED'
const DEFAULT_PAGE_SIZE = 6

type Props = Readonly<{
  canMutate: boolean
}>

export function DirectInstrumentBuildingPanel({ canMutate }: Props) {
  const { requestLiveEdit, liveEditDialog } = useLiveEditEntry()
  const { showToast } = useToast()
  const [statusFilter, setStatusFilter] = useState<DirectInstrumentStatusFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [pauseTarget, setPauseTarget] = useState<IQuickInstrumentListItem | null>(null)
  const [stopTarget, setStopTarget] = useState<IQuickInstrumentListItem | null>(null)
  const [resumeTarget, setResumeTarget] = useState<IQuickInstrumentListItem | null>(null)

  const pauseMutation = usePauseQuickInstrument()
  const stopMutation = useStopQuickInstrument()
  const resumeMutation = useReactivateQuickInstrument()

  useEffect(() => {
    const handle = globalThis.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 300)
    return () => globalThis.clearTimeout(handle)
  }, [searchInput])

  useEffect(() => {
    setPage(0)
  }, [statusFilter])

  const { data, isLoading, isError, error, refetch } = useQuickInstruments({
    status: statusFilter,
    search,
    page: page + 1,
    pageSize,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  const handlePauseConfirm = async () => {
    if (!pauseTarget) return
    try {
      await pauseMutation.mutateAsync(pauseTarget.id)
      showToast('Instrument paused', 'success')
      setPauseTarget(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Unable to pause instrument.'), 'error')
    }
  }

  const handleStopConfirm = async (payload: {
    stopReason: string
    stopReasonOther?: string | null
  }) => {
    if (!stopTarget) return
    try {
      await stopMutation.mutateAsync({
        instrumentId: stopTarget.id,
        stopReason: payload.stopReason,
        stopReasonOther: payload.stopReasonOther,
      })
      showToast('Instrument stopped', 'success')
      setStopTarget(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Unable to stop instrument.'), 'error')
    }
  }

  const resumeInstrument = async (
    item: IQuickInstrumentListItem,
    additionalResponseTarget?: number | null,
  ): Promise<boolean> => {
    try {
      await resumeMutation.mutateAsync({
        instrumentId: item.id,
        body:
          additionalResponseTarget === undefined ? undefined : { additionalResponseTarget },
      })
      showToast('Instrument resumed', 'success')
      return true
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Unable to resume instrument.'), 'error')
      return false
    }
  }

  const handleResume = async (item: IQuickInstrumentListItem) => {
    if (isSignupSurveyPausedAtTarget(item)) {
      setResumeTarget(item)
      return
    }
    await resumeInstrument(item)
  }

  const handleResumeSurveyConfirm = async (additionalResponseTarget: number | null) => {
    if (!resumeTarget) return
    if (await resumeInstrument(resumeTarget, additionalResponseTarget)) setResumeTarget(null)
  }

  return (
    <Box data-testid="direct-instrument-building-panel">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { lg: 'center' },
          gap: 2,
        }}
      >
        <TextField
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by Instrument"
          aria-label="Search by instrument"
          data-testid="direct-instrument-search"
          size="small"
          sx={{
            width: { xs: '100%', lg: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#fff',
              fontSize: '14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box
          role="tablist"
          aria-label="Filter instruments by status"
          data-testid="direct-instrument-status-filter"
          sx={{
            display: 'flex',
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            gap: 1,
            overflowX: { xs: 'auto', sm: 'visible' },
            pb: { xs: 0.5, sm: 0 },
          }}
        >
          {DIRECT_INSTRUMENT_STATUS_FILTERS.map((option) => {
            const selected = statusFilter === option.value
            return (
              <Button
                key={option.value}
                role="tab"
                aria-selected={selected}
                onClick={() => setStatusFilter(option.value)}
                data-testid={`direct-instrument-filter-${option.value}`}
                sx={{
                  flexShrink: 0,
                  borderRadius: '999px',
                  px: 2,
                  py: 1,
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: selected ? VIOLET : '#E2E8F0',
                  bgcolor: selected ? VIOLET : '#fff',
                  color: selected ? '#fff' : '#64748B',
                  '&:hover': {
                    borderColor: selected ? VIOLET : PRIMARY_HOVER,
                    color: selected ? '#fff' : PRIMARY_HOVER,
                    bgcolor: selected ? VIOLET : '#fff',
                  },
                }}
              >
                {option.label}
              </Button>
            )
          })}
        </Box>
      </Box>

      {isError ? (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 6,
          }}
          data-testid="direct-instrument-load-error"
        >
          <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
            {messageFromUnknownApiError(error, 'Unable to load instruments.')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              Promise.resolve(refetch()).catch(() => undefined)
            }}
            data-testid="direct-instrument-retry"
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Retry
          </Button>
        </Box>
      ) : null}

      {!isError && !isLoading && items.length === 0 ? (
        <Box
          data-testid="direct-instrument-empty"
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 3,
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            bgcolor: '#fff',
            color: '#64748B',
            fontSize: '14px',
          }}
        >
          <BlockIcon sx={{ fontSize: 16 }} />
          No instruments match this filter.
        </Box>
      ) : null}

      {!isError && (isLoading || items.length > 0) ? (
        <Box
          data-testid="direct-instrument-grid"
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              xl: 'repeat(3, minmax(0, 1fr))',
            },
            alignItems: 'stretch',
            gap: 3,
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading && items.length === 0
            ? Array.from({ length: 3 }, (_, i) => (
                <Box
                  key={`skel-${i}`}
                  sx={{
                    height: 148,
                    borderRadius: '10px',
                    bgcolor: '#F1F5F9',
                  }}
                />
              ))
            : items.map((item) => (
                <DirectInstrumentCard
                  key={item.id}
                  item={item}
                  canMutate={canMutate}
                  onPause={setPauseTarget}
                  onStop={setStopTarget}
                  onResume={(row) => {
                    void handleResume(row)
                  }}
                  onEditLive={canMutate ? requestLiveEdit : undefined}
                />
              ))}
        </Box>
      ) : null}

      {!isError && total > DEFAULT_PAGE_SIZE ? (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number.parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[6, 12, 18]}
          labelRowsPerPage="Rows per page:"
          data-testid="direct-instrument-pagination"
          sx={{
            mt: 2,
            borderTop: 'none',
            '& .MuiTablePagination-toolbar': { minHeight: 52, px: 0 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '14px',
              color: '#4a5565',
            },
          }}
        />
      ) : null}

      {liveEditDialog}

      <PauseInstrumentDialog
        open={pauseTarget != null}
        onClose={() => setPauseTarget(null)}
        onConfirm={handlePauseConfirm}
        confirming={pauseMutation.isPending}
      />
      <StopInstrumentDialog
        open={stopTarget != null}
        instrumentName={stopTarget?.name ?? ''}
        onClose={() => setStopTarget(null)}
        onConfirm={handleStopConfirm}
        confirming={stopMutation.isPending}
      />
      <ResumeSurveyDialog
        open={resumeTarget != null}
        completedResponses={resumeTarget?.responseCount ?? 0}
        onClose={() => setResumeTarget(null)}
        onConfirm={handleResumeSurveyConfirm}
        confirming={resumeMutation.isPending}
      />
    </Box>
  )
}

const PRIMARY_HOVER = '#2563EB'
